/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetGiftsPermalinkRow, GetGiftsPendingCountRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String, HELPERS } from '../../helpers/index.js';
import { fromBinary } from '@bufbuild/protobuf';
import { decodeRealtimePayload } from '../../devtool/decode-realtime-payload.js';
import { GiftEventSchema } from '@morfi/proto';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';


// ─── useGetGiftsPermalink ────────────────────────────────────────────────────────────

export interface useGetGiftsPermalinkArgs { readonly opaqueId: string; readonly includeClaimed?: string; readonly fields?: readonly ('opaqueId' | 'giftPda' | 'senderProfileUrl' | 'recipientPlatform' | 'recipientHandle' | 'assetKind' | 'mintPubkey' | 'amount' | 'message' | 'createdAt' | 'status' | 'claimedAt' | 'canIndex' | 'viewerIsContributor' | 'sender')[]; }


async function _useGetGiftsPermalinkQueryFn(
  args: useGetGiftsPermalinkArgs,
  ctx: QueryFunctionContext,
): Promise<GetGiftsPermalinkRow | null> {
  if (args.opaqueId === undefined || args.opaqueId === null || args.opaqueId === '') return null;
  const url = `/v1/gifts/permalink/${args.opaqueId}`;
  const params = new URLSearchParams();
  // FQ-DETAIL-SHARED-ENTRY-001 — fetch the full row; `args.fields` is
  // intentionally NOT forwarded (shared id-only cache entry).
  if (args.includeClaimed !== undefined) params.set('includeClaimed', String(args.includeClaimed));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetGiftsPermalink',
    routeTemplate: '/v1/gifts/permalink/{opaqueId}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetGiftsPermalinkRow;
}


const _useGetGiftsPermalinkFactory = Object.assign(
  (args: useGetGiftsPermalinkArgs) => ({
    queryKey: ['gifts', 'detail', args.opaqueId, { includeClaimed: args.includeClaimed ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetGiftsPermalinkQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetGiftsPermalink',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['gifts', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetGiftsPermalinkArgs, GetGiftsPermalinkRow>;


export const useGetGiftsPermalinkSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/gifts/permalink/{opaqueId}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetGiftsPermalinkFactory },
  parseInput: (raw: unknown): useGetGiftsPermalinkArgs => raw as useGetGiftsPermalinkArgs,
  parseResponse: (raw: unknown): GetGiftsPermalinkRow => raw as GetGiftsPermalinkRow,
  specName: 'useGetGiftsPermalink' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
  giftEntity: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetGiftsPermalinkRow>;

function _useGetGiftsPermalinkPerEntitySubjects(args: useGetGiftsPermalinkArgs): readonly string[] {
  const subs: readonly string[] = [`morfi.gift.${args.opaqueId}.event.v0`];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetGiftsPermalinkApplyEvent(
  prev: GetGiftsPermalinkRow | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetGiftsPermalinkArgs,
): GetGiftsPermalinkRow | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetGiftsPermalinkRow = prev;
  // Switch-based dispatcher · one parse per event, zero regex.
  if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
  const _parts = subject.split('.');
  if (_parts.length === 5 && _parts[0] === 'morfi' && _parts[1] === 'gift' && _parts[3] === 'event') {
    switch (_parts[4]) {
      case 'v0': {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(GiftEventSchema, _d)
            : fromBinary(GiftEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof GiftEventSchema>>;
        next = (HELPERS.mapGiftEventKindToPermalinkStatus as unknown as (prev: GetGiftsPermalinkRow, raw: ReturnType<typeof fromBinary<typeof GiftEventSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetGiftsPermalinkRow)(next, raw, args as unknown as Record<string, unknown>);
        next = (HELPERS.setPermalinkClaimedAtIfClaimed as unknown as (prev: GetGiftsPermalinkRow, raw: ReturnType<typeof fromBinary<typeof GiftEventSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetGiftsPermalinkRow)(next, raw, args as unknown as Record<string, unknown>);
        break;
      }
      default: break;
    }
  }
  return next;
}

export const useGetGiftsPermalinkRealtime = {
  domain: 'gifts' as const,
  domainPins: [] as readonly string[],
  rowKey: 'opaqueId' as const,
  perEntitySubjects: (args: useGetGiftsPermalinkArgs) => _useGetGiftsPermalinkPerEntitySubjects(args),
  viewportGated: false as const,
  singleton: true as const,
  applyEvent:
    (args: useGetGiftsPermalinkArgs) =>
    (prev: GetGiftsPermalinkRow | undefined, ev: MorfiEvent<unknown>): GetGiftsPermalinkRow | undefined =>
      _useGetGiftsPermalinkApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, GetGiftsPermalinkRow, unknown>;


// ─── useGetGiftsPendingCount ────────────────────────────────────────────────────────────

export interface useGetGiftsPendingCountArgs { readonly userId?: string; readonly fields?: readonly ('pendingCount' | 'pendingValue')[]; }


async function _useGetGiftsPendingCountQueryFn(
  args: useGetGiftsPendingCountArgs,
  ctx: QueryFunctionContext,
): Promise<GetGiftsPendingCountRow | null> {
  const url = '/v1/gifts/pending-count';
  const params = new URLSearchParams();
  if (args.fields !== undefined) params.set('fields', args.fields.join(','));
  if (args.userId !== undefined) params.set('userId', String(args.userId));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetGiftsPendingCount',
    routeTemplate: '/v1/gifts/pending-count',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetGiftsPendingCountRow;
}


const _useGetGiftsPendingCountFactory = Object.assign(
  (args: useGetGiftsPendingCountArgs) => ({
    queryKey: ['gifts', 'detail', { userId: args.userId ?? null }, args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetGiftsPendingCountQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetGiftsPendingCount',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['gifts', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetGiftsPendingCountArgs, GetGiftsPendingCountRow>;


export const useGetGiftsPendingCountSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/gifts/pending-count',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetGiftsPendingCountFactory },
  parseInput: (raw: unknown): useGetGiftsPendingCountArgs => raw as useGetGiftsPendingCountArgs,
  parseResponse: (raw: unknown): GetGiftsPendingCountRow => raw as GetGiftsPendingCountRow,
  specName: 'useGetGiftsPendingCount' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetGiftsPendingCountRow>;

function _useGetGiftsPendingCountPerEntitySubjects(args: useGetGiftsPendingCountArgs): readonly string[] {
  const subs: readonly string[] = [`morfi.user.${args.userId}.gift.v0`];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetGiftsPendingCountApplyEvent(
  prev: GetGiftsPendingCountRow | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetGiftsPendingCountArgs,
): GetGiftsPendingCountRow | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetGiftsPendingCountRow = prev;
  // Switch-based dispatcher · one parse per event, zero regex.
  if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
  const _parts = subject.split('.');
  if (_parts.length === 5 && _parts[0] === 'morfi' && _parts[1] === 'user' && _parts[3] === 'gift') {
    switch (_parts[4]) {
      case 'v0': {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(GiftEventSchema, _d)
            : fromBinary(GiftEventSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof GiftEventSchema>>;
        next = (HELPERS.applyPendingCountDelta as unknown as (prev: GetGiftsPendingCountRow, raw: ReturnType<typeof fromBinary<typeof GiftEventSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetGiftsPendingCountRow)(next, raw, args as unknown as Record<string, unknown>);
        next = (HELPERS.applyPendingValueDelta as unknown as (prev: GetGiftsPendingCountRow, raw: ReturnType<typeof fromBinary<typeof GiftEventSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetGiftsPendingCountRow)(next, raw, args as unknown as Record<string, unknown>);
        break;
      }
      default: break;
    }
  }
  return next;
}

export const useGetGiftsPendingCountRealtime = {
  domain: 'gifts' as const,
  domainPins: [] as readonly string[],
  rowKey: 'pubkey' as const,
  perEntitySubjects: (args: useGetGiftsPendingCountArgs) => _useGetGiftsPendingCountPerEntitySubjects(args),
  viewportGated: false as const,
  singleton: true as const,
  applyEvent:
    (args: useGetGiftsPendingCountArgs) =>
    (prev: GetGiftsPendingCountRow | undefined, ev: MorfiEvent<unknown>): GetGiftsPendingCountRow | undefined =>
      _useGetGiftsPendingCountApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, GetGiftsPendingCountRow, unknown>;
